import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import ReactDOM from 'react-dom/client'
import SEO, { BreadcrumbComponent } from './SEO'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import PrintRoundedIcon from '@mui/icons-material/PrintRounded'
import MailRoundedIcon from '@mui/icons-material/MailRounded'
import ShareRounded from '@mui/icons-material/ShareRounded'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import FavoriteIcon from '@mui/icons-material/Favorite'
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { activityTracker } from '../services/ActivityTracker'
import {
  Box,
  Avatar,
  IconButton,
  Typography,
  Paper,
  Skeleton,
  Fab,
  Zoom,
  useTheme,
  useMediaQuery,
  Tooltip,
  CircularProgress,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Button,
  Divider,
  Container,
  Card,
  CardContent,
  TextField,
} from '@mui/material'

import apiService from '../services/ApiService'
import { Enhanced404Handler } from './Error404'
import { generateBlogPostSchema } from '../services/schemaGenerators'
import { useAuth } from '../context/AuthContext'
import { parseHtmlSanitizeAddTargetToLinks } from '../services/utils'
import { AnnotatedArticle } from './annotations/EnhancedAnnotatedArticle.js'

import './editor/themes/PlaygroundEditorTheme.css'
import './editor/index.css'

// KaTeX — for rendering equation nodes (data-lexical-equation)
import katex from 'katex'
import 'katex/dist/katex.min.css'
import './editor/nodes/ImageNode.css'
import './editor/themes/blogs-content.css'

import PollViewer from './PollViewer'

// Module-level cache for blog content
const blogCache = {}

const themeColors = {
  primary: '#232536',
  secondary: '#ffcf51',
  hover: '#3a3e5a',
}
const TEAM_GTL_AVATAR_SRC = '/favicon-96x96.png'

const getProxiedUrl = (originalUrl) => {
  if (!originalUrl || typeof originalUrl !== 'string') {
    return ''
  }

  const trimmed = originalUrl.trim()
  if (!trimmed) {
    return ''
  }

  if (trimmed.startsWith('/')) {
    return trimmed
  }

  const lowered = trimmed.toLowerCase()
  if (lowered.startsWith('data:') || lowered.startsWith('blob:')) {
    return trimmed
  }

  if (trimmed.includes('/api/v1/profile/avatar?url=')) {
    return trimmed
  }

  const backendBase = (
    process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080'
  ).replace(/\/$/, '')
  return `${backendBase}/api/v1/profile/avatar?url=${encodeURIComponent(trimmed)}`
}

const isTeamGtlAuthor = (authorType, authorName) => {
  const normalizedAuthorType = (authorType || '')
    .toString()
    .trim()
    .toUpperCase()
  if (normalizedAuthorType === 'TEAM_GTL') {
    return true
  }
  const normalizedAuthorName = (authorName || '')
    .toString()
    .trim()
    .toLowerCase()
  return normalizedAuthorName.includes('team gtl')
}

const isCommunityAuthor = (authorType) => {
  const normalizedAuthorType = (authorType || '')
    .toString()
    .trim()
    .toUpperCase()
  return normalizedAuthorType === 'COMMUNITY' || normalizedAuthorType === 'USER'
}

const getAuthorDisplayName = (blog) => {
  const explicitName = (blog?.author || '').trim()
  if (explicitName) {
    return explicitName
  }
  return isTeamGtlAuthor(blog?.authorType, blog?.author)
    ? 'Team GTL'
    : 'Community'
}

const getAuthorAvatarSource = (blog) => {
  const possibleSources = [
    blog?.authorAvatarUrl,
    blog?.authorImageUrl,
    blog?.authorProfileImageUrl,
    blog?.authorPhotoUrl,
    blog?.profileImageUrl,
    blog?.userAvatarUrl,
    blog?.userProfileImageUrl,
    blog?.authorAvatar,
  ]
  const matched = possibleSources.find(
    (source) => typeof source === 'string' && source.trim(),
  )
  if (matched) {
    return getProxiedUrl(matched)
  }
  return isTeamGtlAuthor(blog?.authorType, blog?.author)
    ? TEAM_GTL_AVATAR_SRC
    : ''
}

const getInitials = (value) => {
  const normalized = (value || '').trim()
  if (!normalized) {
    return 'U'
  }
  const parts = normalized.split(/\s+/).filter(Boolean)
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase()
}
const themedTextFieldSx = {
  '& .MuiInputLabel-root': {
    color: '#5b6172',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: themeColors.primary,
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#cfd6e0',
    },
    '&:hover fieldset': {
      borderColor: themeColors.hover,
    },
    '&.Mui-focused fieldset': {
      borderColor: themeColors.primary,
      borderWidth: 2,
    },
  },
}
const BlogDetail = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuth()
  const [blogContent, setBlogContent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const [relatedBlogs, setRelatedBlogs] = useState([])
  const [comments, setComments] = useState([])
  const [commentText, setCommentText] = useState('')
  const [commentSubmitting, setCommentSubmitting] = useState(false)
  const [replyDrafts, setReplyDrafts] = useState({})
  const [replySubmittingFor, setReplySubmittingFor] = useState('')
  const [expandedCommentReplies, setExpandedCommentReplies] = useState({})
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [relativeTimeNow, setRelativeTimeNow] = useState(() => Date.now())
  const [editingCommentId, setEditingCommentId] = useState('')
  const [editingCommentContent, setEditingCommentContent] = useState('')
  const [commentEditSubmitting, setCommentEditSubmitting] = useState(false)
  const [commentDeleteSubmittingFor, setCommentDeleteSubmittingFor] =
    useState('')
  const [deleteDialogCommentId, setDeleteDialogCommentId] = useState('')
  const [likeState, setLikeState] = useState({ liked: false, likeCount: 0 })

  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState('success')

  const componentRef = useRef()
  const fetchedRef = useRef(false)

  // Theme colors
  const themeColors = {
    primary: '#232536',
    secondary: '#ffcf51',
    hover: '#3a3e5a',
  }
  const theme = useTheme()
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const buttonBorderRadius = '28px'
  const currentUserEmail = (user?.email || '').trim().toLowerCase()

  // Helper function to strip HTML tags from text
  const stripHtml = (html) => {
    if (!html) return ''
    if (typeof html === 'object') return JSON.stringify(html)
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent || div.innerText || ''
  }

  // Snackbar handlers
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return
    setSnackbarOpen(false)
  }

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }

  // Scroll to top function
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [])

  // Show back to top button when scrolled down
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true)
      } else {
        setShowBackToTop(false)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Refresh relative timestamps while the page is open.
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setRelativeTimeNow(Date.now())
    }, 30000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [])

  // Track blog view and check bookmark status
  useEffect(() => {
    if (blogContent?.slug) {
      activityTracker.trackDocumentView(
        'blog',
        blogContent.slug,
        stripHtml(blogContent.title),
        {
          category: blogContent.category,
          tags: blogContent.tags,
          publishedDate: blogContent.publishedDate || blogContent.createdAt,
        },
      )
    }
  }, [blogContent])

  useEffect(() => {
    if (!blogContent) return
    setLikeState({
      liked: Boolean(blogContent.likedByCurrentUser),
      likeCount: blogContent.likeCount || 0,
    })
  }, [blogContent])

  useEffect(() => {
    if (!blogContent?.content) return

    const container = document.querySelector('.blog-content')
    if (!container) return

    const detailsNodes = container.querySelectorAll('details')

    detailsNodes.forEach((detailsEl) => {
      detailsEl.classList.add('Collapsible__container')

      const directChildren = Array.from(detailsEl.children)
      const summaryEl = directChildren.find(
        (child) => child.tagName.toLowerCase() === 'summary',
      )

      if (!summaryEl) {
        return
      }

      summaryEl.classList.add('Collapsible__title')

      // Normalize boolean open attribute to HTML standard.
      if (detailsEl.hasAttribute('open')) {
        const rawOpen = (detailsEl.getAttribute('open') || '').toLowerCase()
        if (rawOpen === 'false' || rawOpen === '0') {
          detailsEl.removeAttribute('open')
        } else {
          detailsEl.setAttribute('open', '')
        }
      }

      const existingContentEl = directChildren.find(
        (child) =>
          child !== summaryEl &&
          child.classList.contains('Collapsible__content'),
      )

      if (existingContentEl) {
        existingContentEl.classList.add('Collapsible__content')
        return
      }

      const contentEl = document.createElement('div')
      contentEl.className = 'Collapsible__content'

      const allNodes = Array.from(detailsEl.childNodes)
      const summaryIndex = allNodes.indexOf(summaryEl)
      const nodesAfterSummary =
        summaryIndex >= 0 ? allNodes.slice(summaryIndex + 1) : []

      nodesAfterSummary.forEach((node) => {
        contentEl.appendChild(node)
      })

      detailsEl.appendChild(contentEl)
    })
  }, [blogContent?.content])

  // Wrap tables with scroll container div.
  // Lexical TableNode doesn't export the wrapper div, so we add it client-side.
  useEffect(() => {
    if (!blogContent?.content) return

    const container = document.querySelector('.blog-content')
    if (!container) return

    // Find all tables that aren't already wrapped
    const tables = container.querySelectorAll(
      'table.PlaygroundEditorTheme__table',
    )
    tables.forEach((table) => {
      // Check if already wrapped
      if (
        table.parentElement?.classList.contains(
          'PlaygroundEditorTheme__tableScrollableWrapper',
        )
      ) {
        return
      }

      // Create wrapper div
      const wrapper = document.createElement('div')
      wrapper.className = 'PlaygroundEditorTheme__tableScrollableWrapper'

      // Insert wrapper before table and move table inside
      table.parentNode.insertBefore(wrapper, table)
      wrapper.appendChild(table)
    })
  }, [blogContent?.content])

  // Render KaTeX equations in the displayed blog content.
  // We find these after the content is injected and call katex.render() on each one.
  useEffect(() => {
    if (!blogContent?.content) return

    const renderEquations = () => {
      const equationEls = document.querySelectorAll(
        '.blog-content [data-lexical-equation]',
      )
      equationEls.forEach((el) => {
        const equation = el.getAttribute('data-lexical-equation')
        const isInline = el.getAttribute('data-lexical-inline') !== 'false'
        if (!equation) return
        try {
          katex.render(equation, el, {
            displayMode: !isInline,
            throwOnError: false,
            errorColor: '#cc0000',
          })
        } catch {
          // leave as raw text if katex fails
        }
      })
    }

    // Defer by one tick to ensure HTML content has been rendered to the DOM
    const id = setTimeout(renderEquations, 0)
    return () => clearTimeout(id)
  }, [blogContent?.content])

  // Mount interactive PollViewer components into .poll-widget placeholders
  useEffect(() => {
    if (!blogContent?.content) return
    const container = document.querySelector('.blog-content')
    if (!container) return

    const pollWidgets = container.querySelectorAll('.poll-widget')
    if (!pollWidgets.length) return

    const roots = []

    pollWidgets.forEach((el) => {
      const question = el.getAttribute('data-poll-question') || ''
      let options = []

      try {
        options = JSON.parse(el.getAttribute('data-poll-options') || '[]')
      } catch { }

      if (!question || !options.length) return

      // Apply alignment from data-poll-format
      const pollFormat = el.getAttribute('data-poll-format') || ''
      el.style.display = 'flex'
      el.style.flexDirection = 'column'
      if (pollFormat === 'center') {
        el.style.alignItems = 'center'
      } else if (pollFormat === 'right') {
        el.style.alignItems = 'flex-end'
      } else {
        el.style.alignItems = 'flex-start'
      }

      // Stable poll ID: blog slug + sanitised question
      const pollId = `${slug}-${question.replace(/\s+/g, '-').toLowerCase().slice(0, 60)}`
      el.innerHTML = ''
      const root = ReactDOM.createRoot(el)
      root.render(<PollViewer pollId={pollId} question={question} options={options} />)
      roots.push(root)
    })

    return () => {
      roots.forEach((root) => {
        try { root.unmount() } catch { }
      })
    }

  }, [blogContent?.content, slug])

  const loadComments = useCallback(
    async ({ silent = false } = {}) => {
      if (!slug || !isAuthenticated) return
      try {
        if (!silent) {
          setCommentsLoading(true)
        }
        const response = await apiService.getBlogComments(slug, {
          bypassCache: true,
          skipCache: true,
        })
        setComments(Array.isArray(response) ? response : [])
      } catch (err) {
        console.error('Failed to load blog comments:', err)
        if (!silent) {
          setComments([])
        }
      } finally {
        if (!silent) {
          setCommentsLoading(false)
        }
      }
    },
    [slug, isAuthenticated],
  )

  // Fetch blog content and related blogs
  useEffect(() => {
    const getBlogContent = async () => {
      if (!slug || fetchedRef.current) return

      setLoading(true)
      fetchedRef.current = true

      try {
        let data

        // Check cache first
        if (blogCache[slug]) {
          data = blogCache[slug]
          setBlogContent(data)
          setLoading(false)
        } else {
          // Fetch from API
          data = await apiService.getBlogBySlug(slug)
          blogCache[slug] = data
          setBlogContent(data)
          setLoading(false)
        }

        setError(null)

        // Fetch related blogs by category
        if (data.category) {
          try {
            const relatedResponse = await apiService.getBlogsByCategory(
              data.category,
              1,
              5,
              data.slug,
            )
            // Filter out the current blog from related blogs
            const filtered = relatedResponse.content.filter(
              (blog) => blog.slug !== slug,
            )
            setRelatedBlogs(filtered.slice(0, 3))
          } catch (relatedError) {
            console.error('Error fetching related blogs:', relatedError)
            setRelatedBlogs([]) // Ensure state is reset on error
          }
        } else {
          // console.log('No category found, clearing related blogs')

          setRelatedBlogs([])
        }
      } catch (err) {
        console.error('Error fetching blog content:', err.message)
        setError(err.message)
        setRelatedBlogs([]) // Reset on main error too
        fetchedRef.current = false

        setLoading(false)
      }
    }

    setRelatedBlogs([])
    getBlogContent()

    return () => {
      fetchedRef.current = false
    }
  }, [slug])

  useEffect(() => {
    if (!slug) return undefined

    if (!isAuthenticated) {
      setComments([])
      setCommentsLoading(false)
      return undefined
    }

    loadComments()
    const intervalId = window.setInterval(() => {
      loadComments({ silent: true })
    }, 12000)

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadComments({ silent: true })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [slug, isAuthenticated, loadComments])

  useEffect(() => {
    const environment = process.env.REACT_APP_ENVIRONMENT || 'dev'
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = ``
    link.id = 'external-css'

    if (!document.getElementById('external-css')) {
      document.head.appendChild(link)
    }

    return () => {
      document.getElementById('external-css')?.remove() // Cleanup on unmount
    }
  }, [])

  // Generate SEO data
  const generateSEOData = useCallback(() => {
    if (!blogContent) return null

    const seoTitle = blogContent.title
      ? `${stripHtml(blogContent.title)} | GCC Tax Laws Blog`
      : `Tax Insights | GCC Tax Laws Blog`

    const seoDescription =
      blogContent.excerpt ||
      blogContent.description ||
      (blogContent.content
        ? stripHtml(blogContent.content).substring(0, 155) + '...'
        : 'Stay informed with the latest tax developments and insights from across the GCC region.')

    const canonicalUrl = `https://gcctaxlaws.com/blogs/${blogContent.slug}`

    const keywords = [
      'GCC tax blog',
      'tax insights',
      'tax updates',
      blogContent.category,
      ...(blogContent.tags || []),
      'Middle East tax',
      'tax compliance',
    ]
      .filter(Boolean)
      .join(', ')

    const schemaMarkup = generateBlogPostSchema(blogContent)

    return {
      title: seoTitle,
      description: seoDescription,
      canonical: canonicalUrl,
      keywords,
      schemaMarkup,
      publishedDate: blogContent.publishedDate || blogContent.createdAt,
      modifiedDate: blogContent.updatedDate || blogContent.updatedAt,
    }
  }, [blogContent])

  // Handle sharing
  const handleShare = useCallback(async () => {
    const url = `${window.location.origin}/blogs/${blogContent?.slug}`
    const title = blogContent?.title || 'Blog Post'
    const cleanTitle = stripHtml(title)

    const shareData = {
      title: cleanTitle,
      text: `Check out this blog post: ${cleanTitle}`,
      url: url,
    }

    try {
      let shareMethod = 'shared via link'
      if (navigator.share) {
        shareMethod = 'shared via native api'
        await navigator.share(shareData)
        showSnackbar('Blog post shared successfully!', 'success')
      } else {
        await navigator.clipboard.writeText(url)
        showSnackbar('Blog link copied to clipboard!', 'success')
      }

      activityTracker.trackDocumentAction(
        shareMethod,
        'blog',
        blogContent?.slug,
        cleanTitle,
      )
    } catch (error) {
      if (error.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(url)
          showSnackbar('Blog link copied to clipboard!', 'success')
        } catch (clipboardError) {
          showSnackbar('Failed to share blog post', 'error')
        }
      }
    }
  }, [blogContent])

  // Handle print
  const handlePrint = useCallback(() => {
    if (componentRef.current) {
      const printWindow = window.open('', '_blank')
      if (!printWindow) return

      const printDoc = printWindow.document
      printDoc.title = 'GCC Tax Laws - Blog'
      printDoc.body.replaceChildren()

      const charsetMeta = printDoc.createElement('meta')
      charsetMeta.setAttribute('charset', 'UTF-8')
      printDoc.head.appendChild(charsetMeta)

      const viewportMeta = printDoc.createElement('meta')
      viewportMeta.setAttribute('name', 'viewport')
      viewportMeta.setAttribute(
        'content',
        'width=device-width, initial-scale=1.0',
      )
      printDoc.head.appendChild(viewportMeta)

      // document.styleSheets enumerates every active sheet;
      const styleLoadPromises = []
      const seenHrefs = new Set()
      Array.from(document.styleSheets).forEach((sheet) => {
        try {
          if (sheet.disabled) return
          if (sheet.href) {
            if (seenHrefs.has(sheet.href)) return
            seenHrefs.add(sheet.href)
            const link = printDoc.createElement('link')
            link.rel = 'stylesheet'
            link.type = 'text/css'
            link.href = sheet.href
            const loaded = new Promise((resolve) => {
              link.onload = resolve
              link.onerror = resolve
              setTimeout(resolve, 1500)
            })
            styleLoadPromises.push(loaded)
            printDoc.head.appendChild(link)
          } else {
            const styleEl = printDoc.createElement('style')
            if (sheet.cssRules && sheet.cssRules.length > 0) {
              styleEl.textContent = Array.from(sheet.cssRules)
                .map((rule) => rule.cssText)
                .join('\n')
            } else if (sheet.ownerNode?.textContent) {
              styleEl.textContent = sheet.ownerNode.textContent
            }
            printDoc.head.appendChild(styleEl)
          }
        } catch (_e) {
          // CORS-blocked sheets are inaccessible via cssRules but are
          // already linked via their href branch above skip silently.
        }
      })

      const printStyle = printDoc.createElement('style')
      printStyle.textContent = `
        @media print {
          /* Hide elements that shouldn't be printed */
          .MuiFab-root,
          .print-hide,
          button,
          .MuiIconButton-root,
          .MuiTooltip-tooltip,
          .MuiSnackbar-root {
            display: none !important;
          }

          /* Better typography for print */
          body {
            font-size: 12pt !important;
            line-height: 1.4 !important;
            color: black !important;
            background: white !important;
          }

          h1, h2, h3, h4, h5, h6 {
            page-break-after: avoid;
            page-break-inside: avoid;
          }

          /* Remove shadows and borders for print */
          .MuiPaper-root {
            box-shadow: none !important;
            border: none !important;
          }

          /* Ensure links are visible in print */
          a {
            color: black !important;
            text-decoration: underline !important;
          }

          /* Hide header actions */
          .blog-header-actions {
            display: none !important;
          }

          /* Optimize related articles for print */
          .related-articles .MuiCard-root {
            border: 1px solid #ccc !important;
            margin-bottom: 10px !important;
            page-break-inside: avoid;
          }

          img {
            max-width: 100% !important;
            height: auto !important;
            page-break-inside: avoid;
          }

          .PlaygroundEditorTheme__tableScrollableWrapper,
          .blog-content .PlaygroundEditorTheme__tableScrollableWrapper {
            overflow: visible !important;
            overflow-x: visible !important;
            overflow-y: visible !important;
            width: 100% !important;
            max-width: 100% !important;
            box-shadow: none !important;
            display: block !important;
            page-break-inside: avoid;
          }

          .PlaygroundEditorTheme__table,
          .blog-content table,
          .blog-content .PlaygroundEditorTheme__table {
            width: 100% !important;
            max-width: 100% !important;
            table-layout: auto !important;
            overflow: visible !important;
            page-break-inside: auto;
            font-size: 9pt !important;
          }

          .PlaygroundEditorTheme__tableCell,
          .PlaygroundEditorTheme__tableCellHeader,
          .blog-content td,
          .blog-content th {
            white-space: normal !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            font-size: 9pt !important;
            padding: 4px 6px !important;
          }

          .PlaygroundEditorTheme__table tr,
          .blog-content tr {
            page-break-inside: avoid;
          }

          .PlaygroundEditorTheme__layoutContainer,
          .blog-content .PlaygroundEditorTheme__layoutContainer {
            overflow: visible !important;
            overflow-x: visible !important;
            width: 100% !important;
            display: grid !important;
            page-break-inside: avoid;
          }

          .PlaygroundEditorTheme__layoutItem,
          .blog-content .PlaygroundEditorTheme__layoutItem {
            overflow: visible !important;
            min-width: 0 !important;
            page-break-inside: avoid;
          }

          .PlaygroundEditorTheme__code,
          .blog-content pre {
            overflow: visible !important;
            overflow-x: visible !important;
            white-space: pre-wrap !important;
            word-break: break-all !important;
            page-break-inside: avoid;
          }

          .katex-display {
            overflow: visible !important;
          }
        }
      `
      printDoc.head.appendChild(printStyle)

      const contentClone = componentRef.current.cloneNode(true)

      const headerActions = contentClone.querySelector('.blog-header-actions')
      if (headerActions) {
        headerActions.remove()
      }

      const elementsToRemove = contentClone.querySelectorAll(
        '.MuiFab-root, .MuiTooltip-tooltip, .MuiSnackbar-root, button:not(.blog-content button)',
      )
      elementsToRemove.forEach((el) => el.remove())

      const scrollableEls = contentClone.querySelectorAll(
        '.PlaygroundEditorTheme__tableScrollableWrapper, .PlaygroundEditorTheme__layoutContainer',
      )
      scrollableEls.forEach((el) => {
        el.style.overflow = 'visible'
        el.style.overflowX = 'visible'
        el.style.overflowY = 'visible'
        el.style.maxWidth = '100%'
        el.style.width = '100%'
        el.style.boxShadow = 'none'
      })

      const tableEls = contentClone.querySelectorAll(
        '.PlaygroundEditorTheme__table, .PlaygroundEditorTheme__tableScrollableWrapper > table',
      )
      tableEls.forEach((el) => {
        el.style.width = '100%'
        el.style.maxWidth = '100%'
        el.style.tableLayout = 'auto'
        el.style.overflow = 'visible'
      })

      const printHeader = printDoc.createElement('div')
      printHeader.style.marginBottom = '20px'
      printHeader.style.paddingBottom = '10px'
      printHeader.style.borderBottom = '2px solid #333'

      const titleEl = printDoc.createElement('h1')
      titleEl.style.margin = '0'
      titleEl.style.color = '#232536'
      titleEl.textContent = 'GCC Tax Laws - Blog'

      const dateEl = printDoc.createElement('p')
      dateEl.style.margin = '5px 0'
      dateEl.style.color = '#666'
      dateEl.textContent = `Printed on ${new Date().toLocaleDateString()}`

      printHeader.appendChild(titleEl)
      printHeader.appendChild(dateEl)
      printDoc.body.appendChild(printHeader)
      printDoc.body.appendChild(contentClone)

      const triggerPrint = () => {
        let isClosed = false
        const closePrintWindow = () => {
          if (isClosed || printWindow.closed) return
          isClosed = true
          printWindow.close()
        }

        printWindow.onafterprint = closePrintWindow

        let hasBlurredForPrintDialog = false
        const onBlur = () => {
          hasBlurredForPrintDialog = true
        }
        const onFocus = () => {
          if (hasBlurredForPrintDialog) {
            setTimeout(closePrintWindow, 150)
          }
        }
        printWindow.addEventListener('blur', onBlur, { once: true })
        printWindow.addEventListener('focus', onFocus, { once: true })

        printWindow.focus()
        printWindow.print()

        activityTracker.trackDocumentAction(
          'print',
          'blog',
          blogContent?.slug,
          stripHtml(blogContent?.title),
        )
      }

      Promise.all(styleLoadPromises).finally(() => {
        setTimeout(() => {
          if (printWindow.requestAnimationFrame) {
            printWindow.requestAnimationFrame(triggerPrint)
          } else {
            triggerPrint()
          }
        }, 100)
      })
    }
  }, [blogContent])
  // Handle email
  const handleEmail = () => {
    const subject = encodeURIComponent(
      `GCC Tax Laws Blog: ${stripHtml(blogContent?.title || 'Blog Post')}`,
    )
    const body = encodeURIComponent(
      `I thought you might find this blog post interesting:\n\n${window.location.href}`,
    )
    window.location.href = `mailto:?subject=${subject}&body=${body}`
  }

  const handleToggleLike = async () => {
    if (!blogContent?.slug) return
    try {
      const response = await apiService.toggleBlogLike(blogContent.slug)
      setLikeState({
        liked: Boolean(response?.liked),
        likeCount: response?.likeCount || 0,
      })
    } catch (err) {
      console.error('Failed to toggle like:', err)
      showSnackbar('Failed to update like status', 'error')
    }
  }

  const handleAddComment = async () => {
    const content = commentText.trim()
    if (!blogContent?.slug || !content) return
    try {
      setCommentSubmitting(true)
      await apiService.addBlogComment(blogContent.slug, { content })
      setCommentText('')
      await loadComments({ silent: true })
      showSnackbar('Comment posted', 'success')
    } catch (err) {
      console.error('Failed to post comment:', err)
      showSnackbar('Failed to post comment', 'error')
    } finally {
      setCommentSubmitting(false)
    }
  }

  const closeReplyBox = (commentId) => {
    setReplyDrafts((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, commentId)) {
        return prev
      }
      const next = { ...prev }
      delete next[commentId]
      return next
    })
  }

  const handleReplySubmit = async (parentCommentId) => {
    const content = (replyDrafts[parentCommentId] || '').trim()
    if (!blogContent?.slug || !parentCommentId || !content) return

    try {
      setReplySubmittingFor(parentCommentId)
      await apiService.addBlogComment(blogContent.slug, {
        content,
        parentCommentId,
      })
      closeReplyBox(parentCommentId)
      await loadComments({ silent: true })
      showSnackbar('Reply posted', 'success')
    } catch (err) {
      console.error('Failed to post reply:', err)
      showSnackbar('Failed to post reply', 'error')
    } finally {
      setReplySubmittingFor('')
    }
  }

  const canManageComment = (comment) =>
    Boolean(currentUserEmail) &&
    Boolean(comment?.userEmail) &&
    comment.userEmail.trim().toLowerCase() === currentUserEmail

  const startCommentEdit = (comment) => {
    if (!comment?.id) return
    setEditingCommentId(comment.id)
    setEditingCommentContent(comment.content || '')
  }

  const cancelCommentEdit = () => {
    setEditingCommentId('')
    setEditingCommentContent('')
  }

  const handleCommentEditSubmit = async (commentId) => {
    const content = editingCommentContent.trim()
    if (!blogContent?.slug || !commentId || !content) return

    try {
      setCommentEditSubmitting(true)
      await apiService.updateBlogComment(blogContent.slug, commentId, {
        content,
      })
      cancelCommentEdit()
      await loadComments({ silent: true })
      showSnackbar('Comment updated', 'success')
    } catch (err) {
      console.error('Failed to update comment:', err)
      const message = err?.response?.data?.message || 'Failed to update comment'
      showSnackbar(message, 'error')
    } finally {
      setCommentEditSubmitting(false)
    }
  }

  const openDeleteCommentDialog = (commentId) => {
    if (!commentId) return
    setDeleteDialogCommentId(commentId)
  }

  const closeDeleteCommentDialog = () => {
    if (commentDeleteSubmittingFor) return
    setDeleteDialogCommentId('')
  }

  const confirmDeleteComment = async () => {
    const commentId = deleteDialogCommentId
    if (!blogContent?.slug || !commentId) return
    try {
      setCommentDeleteSubmittingFor(commentId)
      await apiService.deleteBlogComment(blogContent.slug, commentId)
      cancelCommentEdit()
      await loadComments({ silent: true })
      showSnackbar('Comment deleted', 'success')
      setDeleteDialogCommentId('')
    } catch (err) {
      console.error('Failed to delete comment:', err)
      const message = err?.response?.data?.message || 'Failed to delete comment'
      showSnackbar(message, 'error')
    } finally {
      setCommentDeleteSubmittingFor('')
    }
  }

  const updateCommentLikeInTree = (items, commentId, updater) =>
    (items || []).map((item) => {
      const nextItem = item.id === commentId ? updater(item) : item

      const nextReplies = item.replies?.length
        ? updateCommentLikeInTree(item.replies, commentId, updater)
        : item.replies

      if (nextItem === item && nextReplies === item.replies) {
        return item
      }

      return {
        ...nextItem,
        replies: nextReplies,
      }
    })

  const handleToggleCommentLike = async (commentId) => {
    if (!blogContent?.slug || !commentId) return
    try {
      const response = await apiService.toggleBlogCommentLike(
        blogContent.slug,
        commentId,
      )
      const liked = Boolean(response?.liked)
      const likeCount = Number.isFinite(response?.likeCount)
        ? response.likeCount
        : 0

      setComments((prev) =>
        updateCommentLikeInTree(prev, commentId, (comment) => ({
          ...comment,
          likedByCurrentUser: liked,
          likeCount,
        })),
      )
    } catch (err) {
      console.error('Failed to toggle comment like:', err)
      showSnackbar('Failed to update comment like', 'error')
    }
  }

  const isCommentRepliesExpanded = (commentId) =>
    expandedCommentReplies[commentId] !== false

  const toggleCommentReplies = (commentId) => {
    setExpandedCommentReplies((prev) => ({
      ...prev,
      [commentId]: !(prev[commentId] !== false),
    }))
  }

  // Navigate back to blogs listing
  const handleBackToBlogs = () => {
    const normalizedAuthorType = (blogContent?.authorType || '')
      .toString()
      .trim()
      .toUpperCase()

    const section =
      normalizedAuthorType === 'COMMUNITY' || normalizedAuthorType === 'USER'
        ? 'community'
        : 'team'

    navigate(`/blogs?section=${section}`)
  }

  const parseDateValue = (dateValue) => {
    if (Array.isArray(dateValue)) {
      const [
        year,
        month,
        day,
        hour = 0,
        minute = 0,
        second = 0,
        nanosecond = 0,
      ] = dateValue
      const millisecond = Math.floor(Number(nanosecond || 0) / 1000000)
      // Backend sends UTC timestamps; parse arrays as UTC to preserve instant.
      return new Date(
        Date.UTC(year, month - 1, day, hour, minute, second, millisecond),
      )
    }

    if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      return new Date(dateValue)
    }

    if (dateValue instanceof Date) {
      return dateValue
    }

    return null
  }

  // Format date helper
  const formatDate = (dateValue) => {
    try {
      const date = parseDateValue(dateValue)
      if (!date) {
        return 'Date unavailable'
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Date unavailable'
      }

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch (error) {
      console.error('Date formatting error:', error)
      return 'Date unavailable'
    }
  }

  const formatRelativeCommentTime = (dateValue) => {
    try {
      const date = parseDateValue(dateValue)
      if (!date || isNaN(date.getTime())) {
        return 'Date unavailable'
      }

      const diffSeconds = Math.round((date.getTime() - relativeTimeNow) / 1000)
      const absSeconds = Math.abs(diffSeconds)
      if (absSeconds < 5) {
        return 'just now'
      }

      const relativeTimeFormatter = new Intl.RelativeTimeFormat('en', {
        numeric: 'auto',
      })

      if (absSeconds < 60) {
        return relativeTimeFormatter.format(diffSeconds, 'second')
      }

      const diffMinutes = Math.round(diffSeconds / 60)
      if (Math.abs(diffMinutes) < 60) {
        return relativeTimeFormatter.format(diffMinutes, 'minute')
      }

      const diffHours = Math.round(diffMinutes / 60)
      if (Math.abs(diffHours) < 24) {
        return relativeTimeFormatter.format(diffHours, 'hour')
      }

      const diffDays = Math.round(diffHours / 24)
      if (Math.abs(diffDays) < 7) {
        return relativeTimeFormatter.format(diffDays, 'day')
      }

      const diffWeeks = Math.round(diffDays / 7)
      if (Math.abs(diffDays) < 30) {
        return relativeTimeFormatter.format(diffWeeks, 'week')
      }

      const diffMonths = Math.round(diffDays / 30)
      if (Math.abs(diffDays) < 365) {
        return relativeTimeFormatter.format(diffMonths, 'month')
      }

      const diffYears = Math.round(diffDays / 365)
      return relativeTimeFormatter.format(diffYears, 'year')
    } catch (error) {
      console.error('Relative date formatting error:', error)
      return 'Date unavailable'
    }
  }

  // Calculate reading time
  const calculateReadingTime = (content) => {
    if (!content) return null
    const wordsPerMinute = 200
    const textContent = stripHtml(content)
    const wordCount = textContent.split(/\s+/).length
    return Math.ceil(wordCount / wordsPerMinute)
  }

  const renderCommentTree = (items, depth = 0) => {
    if (!items || items.length === 0) return null

    return items.map((comment) => {
      const hasReplies =
        Array.isArray(comment.replies) && comment.replies.length > 0
      const repliesExpanded = isCommentRepliesExpanded(comment.id)
      const canUserManageComment = canManageComment(comment)
      const isEditingThisComment = editingCommentId === comment.id
      const isDeletingThisComment = commentDeleteSubmittingFor === comment.id

      return (
        <Box
          key={comment.id}
          sx={{
            pl: depth > 0 ? { xs: 2, md: 4 } : 0,
            borderLeft: depth > 0 ? '2px solid #e5e7eb' : 'none',
            mt: 1.5,
          }}
        >
          <Typography
            variant='subtitle2'
            sx={{ color: '#232536', fontWeight: 600 }}
          >
            {comment.userName || comment.userEmail}
          </Typography>
          <Typography
            variant='caption'
            color='text.secondary'
            title={formatDate(comment.createdAt)}
          >
            {formatRelativeCommentTime(comment.createdAt)}
          </Typography>

          {isEditingThisComment ? (
            <Box sx={{ mt: 1, mb: 1 }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={editingCommentContent}
                onChange={(event) =>
                  setEditingCommentContent(event.target.value)
                }
                disabled={commentEditSubmitting}
                sx={themedTextFieldSx}
              />
              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                }}
              >
                <Button
                  size='small'
                  onClick={cancelCommentEdit}
                  disabled={commentEditSubmitting}
                  sx={{ textTransform: 'none', color: '#64748b' }}
                >
                  Cancel
                </Button>
                <Button
                  size='small'
                  variant='contained'
                  disabled={
                    commentEditSubmitting || !editingCommentContent.trim()
                  }
                  onClick={() => handleCommentEditSubmit(comment.id)}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: themeColors.primary,
                    '&:hover': { backgroundColor: themeColors.hover },
                  }}
                >
                  {commentEditSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography
              variant='body2'
              sx={{
                mt: 0.5,
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {comment.content}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 0.75,
              flexWrap: 'wrap',
            }}
          >
            <Button
              size='small'
              disabled={isDeletingThisComment || isEditingThisComment}
              onClick={() => handleToggleCommentLike(comment.id)}
              startIcon={
                comment.likedByCurrentUser ? (
                  <ThumbUpIcon
                    fontSize='small'
                    sx={{ color: themeColors.primary }}
                  />
                ) : (
                  <ThumbUpOffAltIcon
                    fontSize='small'
                    sx={{ color: themeColors.primary, opacity: 0.7 }}
                  />
                )
              }
              sx={{
                textTransform: 'none',
                color: themeColors.primary,
                opacity: comment.likedByCurrentUser ? 1 : 0.8,
                minWidth: 'auto',
                px: 1,
              }}
            >
              {comment.likeCount || 0}
            </Button>
            <Button
              size='small'
              startIcon={<ReplyRoundedIcon fontSize='small' />}
              disabled={isDeletingThisComment || isEditingThisComment}
              onClick={() =>
                setReplyDrafts((prev) => ({
                  ...prev,
                  [comment.id]: prev[comment.id] || '',
                }))
              }
              sx={{
                textTransform: 'none',
                color: '#475569',
                minWidth: 'auto',
                px: 1,
              }}
            >
              Reply
            </Button>

            {canUserManageComment && !isEditingThisComment && (
              <Button
                size='small'
                startIcon={<EditRoundedIcon fontSize='small' />}
                onClick={() => startCommentEdit(comment)}
                disabled={isDeletingThisComment}
                sx={{
                  textTransform: 'none',
                  color: '#475569',
                  minWidth: 'auto',
                  px: 1,
                }}
              >
                Edit
              </Button>
            )}

            {canUserManageComment && !isEditingThisComment && (
              <Button
                size='small'
                startIcon={<DeleteOutlineRoundedIcon fontSize='small' />}
                onClick={() => openDeleteCommentDialog(comment.id)}
                disabled={isDeletingThisComment}
                sx={{
                  textTransform: 'none',
                  color: '#b42318',
                  minWidth: 'auto',
                  px: 1,
                }}
              >
                {isDeletingThisComment ? 'Deleting...' : 'Delete'}
              </Button>
            )}

            {hasReplies && (
              <Button
                size='small'
                startIcon={
                  repliesExpanded ? (
                    <ExpandLessRoundedIcon fontSize='small' />
                  ) : (
                    <ExpandMoreRoundedIcon fontSize='small' />
                  )
                }
                onClick={() => toggleCommentReplies(comment.id)}
                disabled={isDeletingThisComment}
                sx={{
                  textTransform: 'none',
                  color: '#475569',
                  minWidth: 'auto',
                  px: 1,
                }}
              >
                {repliesExpanded
                  ? `Hide replies (${comment.replies.length})`
                  : `Show replies (${comment.replies.length})`}
              </Button>
            )}
          </Box>

          {!isEditingThisComment &&
            Object.prototype.hasOwnProperty.call(replyDrafts, comment.id) && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder='Write a reply...'
                  value={replyDrafts[comment.id] || ''}
                  onChange={(event) =>
                    setReplyDrafts((prev) => ({
                      ...prev,
                      [comment.id]: event.target.value,
                    }))
                  }
                  sx={themedTextFieldSx}
                />
                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                  }}
                >
                  <Button
                    size='small'
                    onClick={() => closeReplyBox(comment.id)}
                    sx={{ textTransform: 'none', color: '#64748b' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size='small'
                    variant='contained'
                    disabled={
                      replySubmittingFor === comment.id ||
                      !(replyDrafts[comment.id] || '').trim()
                    }
                    onClick={() => handleReplySubmit(comment.id)}
                    sx={{
                      textTransform: 'none',
                      backgroundColor: themeColors.primary,
                      '&:hover': { backgroundColor: themeColors.hover },
                    }}
                  >
                    {replySubmittingFor === comment.id
                      ? 'Posting...'
                      : 'Post Reply'}
                  </Button>
                </Box>
              </Box>
            )}
          {hasReplies && (
            <Collapse in={repliesExpanded} timeout='auto' unmountOnExit>
              {renderCommentTree(comment.replies, depth + 1)}
            </Collapse>
          )}
        </Box>
      )
    })
  }

  const seoData = generateSEOData()

  if (loading) {
    return (
      <Container maxWidth='lg'>
        <Box sx={{ padding: 2, margin: 2 }}>
          <Skeleton variant='rectangular' width='100%' height={300} />
          <Box sx={{ mt: 2 }}>
            <Skeleton variant='text' width='60%' height={40} />
            <Skeleton variant='text' width='40%' height={20} />
            <Skeleton variant='text' width='80%' height={20} />
            <Skeleton variant='text' width='90%' height={20} />
          </Box>
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Enhanced404Handler
        error={error}
        blog={{ slug }}
        showFullDetails={process.env.REACT_APP_ENVIRONMENT === 'dev'}
      />
    )
  }

  return (
    <>
      {/* SEO Component */}
      {seoData && (
        <SEO
          title={seoData.title}
          description={seoData.description}
          canonical={seoData.canonical}
          keywords={seoData.keywords}
          schemaMarkup={seoData.schemaMarkup}
          publishedDate={seoData.publishedDate}
          modifiedDate={seoData.modifiedDate}
          articleType='blog'
        />
      )}

      {blogContent && (
        <BreadcrumbComponent currentItem={blogContent} itemType='blog' />
      )}

      <Container maxWidth='lg' sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Paper
          ref={componentRef}
          sx={{
            padding: { xs: 1.5, sm: 2.5, md: 4, lg: 5 },
            margin: { xs: 1, sm: 1.5, md: 2 },
            borderRadius: { xs: '8px', md: '12px' },
          }}
        >
          {/* Header Actions */}
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            className='blog-header-actions'
            sx={{
              mb: { xs: 2, md: 3 },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 0 },
              alignItems: { xs: 'stretch', sm: 'center' },
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToBlogs}
              sx={{
                color: themeColors.primary,
                '&:hover': { backgroundColor: 'rgba(35, 37, 54, 0.04)' },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                padding: { xs: '8px 12px', sm: '8px 16px' },
                alignSelf: { xs: 'flex-start', sm: 'center' },
              }}
            >
              Back to Blogs
            </Button>

            <Box
              sx={{
                display: 'flex',
                gap: { xs: 0.5, sm: 1 },
                alignSelf: { xs: 'flex-end', sm: 'center' },
                alignItems: 'center',
              }}
            >
              <Tooltip title='Like this blog post' arrow>
                <IconButton
                  aria-label='Like this blog post'
                  onClick={handleToggleLike}
                  size={isMobileScreen ? 'small' : 'medium'}
                >
                  {likeState.liked ? (
                    <FavoriteIcon sx={{ color: '#ef4444' }} />
                  ) : (
                    <FavoriteBorderIcon sx={{ color: themeColors.primary }} />
                  )}
                </IconButton>
              </Tooltip>
              <Typography
                variant='caption'
                sx={{ minWidth: 20, color: 'text.secondary' }}
              >
                {likeState.likeCount}
              </Typography>

              <Tooltip title='Print this blog post' arrow>
                <IconButton
                  aria-label='Print this blog post'
                  onClick={handlePrint}
                  size={isMobileScreen ? 'small' : 'medium'}
                >
                  <PrintRoundedIcon sx={{ color: themeColors.primary }} />
                </IconButton>
              </Tooltip>

              <Tooltip title='Share this blog post' arrow>
                <IconButton
                  aria-label='Share this blog post'
                  onClick={handleShare}
                  size={isMobileScreen ? 'small' : 'medium'}
                >
                  <ShareRounded sx={{ color: themeColors.primary }} />
                </IconButton>
              </Tooltip>

              <Tooltip title='Mail this blog post' arrow>
                <IconButton
                  aria-label='Mail this blog post'
                  onClick={handleEmail}
                  size={isMobileScreen ? 'small' : 'medium'}
                >
                  <MailRoundedIcon sx={{ color: themeColors.primary }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {blogContent && (
            <Box>
              {/* Blog Header */}
              <Box mb={{ xs: 3, md: 4 }}>
                {/* Category and Beta Badge */}
                <Box
                  sx={{
                    mb: { xs: 1.5, md: 2 },
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: { xs: 0.5, sm: 1 },
                  }}
                >
                  {blogContent.category && (
                    <Chip
                      label={blogContent.category}
                      size={isMobileScreen ? 'small' : 'medium'}
                      sx={{
                        backgroundColor: 'rgba(255, 218, 27, 0.1)',
                        color: '#23252a',
                        fontWeight: 600,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    />
                  )}
                  {blogContent.betaVersion && (
                    <Chip
                      label='Beta'
                      size={isMobileScreen ? 'small' : 'medium'}
                      sx={{
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        color: '#3b82f6',
                        fontWeight: 600,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    />
                  )}
                </Box>

                {/* Title */}
                <Typography
                  component='h1'
                  sx={{
                    fontSize: {
                      xs: '1.5rem', // 24px - mobile
                      sm: '1.875rem', // 30px - small tablet
                      md: '2.25rem', // 36px - tablet
                      lg: '2.5rem', // 40px - desktop
                    },
                    fontWeight: { xs: 600, md: 700 },
                    lineHeight: { xs: 1.3, md: 1.2 },
                    mb: { xs: 1, md: 1.5 },
                    color: 'text.primary',
                    wordBreak: 'break-word',
                    hyphens: 'auto',
                  }}
                >
                  {stripHtml(blogContent.title) || 'Untitled Blog Post'}
                </Typography>

                {blogContent.subtitle && (
                  <Typography
                    component='h2'
                    sx={{
                      fontSize: {
                        xs: '1.125rem', // 18px - mobile
                        sm: '1.25rem', // 20px - small tablet
                        md: '1.375rem', // 22px - tablet
                        lg: '1.5rem', // 24px - desktop
                      },
                      fontWeight: { xs: 400, md: 500 },
                      lineHeight: { xs: 1.4, md: 1.3 },
                      mb: { xs: 2, md: 3 },
                      color: 'text.secondary',
                      wordBreak: 'break-word',
                      hyphens: 'auto',
                    }}
                  >
                    {stripHtml(blogContent.subtitle)}
                  </Typography>
                )}
                <Divider
                  sx={{
                    borderBottomWidth: '3px',
                    borderColor: '#00343d',
                    mb: { xs: 0.75, md: 1 },
                  }}
                />

                {/* Excerpt/Description */}
                {(blogContent.excerpt || blogContent.description) && (
                  <Box
                    component='div'
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 400,
                      lineHeight: { xs: 1.5, md: 1.6 },
                      mb: { xs: 2, md: 3 },
                      fontSize: {
                        xs: '1rem', // 16px - mobile
                        sm: '1.0625rem', // 17px - small tablet
                        md: '1.125rem', // 18px - desktop
                      },
                    }}
                  >
                    {parseHtmlSanitizeAddTargetToLinks(
                      blogContent.excerpt || blogContent.description,
                      null, // searchQuery
                      `/blogs/${blogContent.slug}`,
                      {
                        fullDocumentContent: blogContent.content,
                        documentTitle:
                          stripHtml(blogContent.title) || 'Blog Post',
                        showSnackbar: showSnackbar,
                      },
                    )}
                  </Box>
                )}

                {/* Meta Information */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    flexWrap: 'wrap',
                    gap: { xs: 1, sm: 1.5, md: 3 },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    color: 'text.secondary',
                    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                  }}
                >
                  {/* Author */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      minWidth: 'fit-content',
                    }}
                  >
                    <Avatar
                      src={getAuthorAvatarSource(blogContent) || undefined}
                      alt={getAuthorDisplayName(blogContent)}
                      imgProps={{ referrerPolicy: 'no-referrer' }}
                      sx={{
                        width: { xs: 22, sm: 24 },
                        height: { xs: 22, sm: 24 },
                        fontSize: { xs: '0.68rem', sm: '0.72rem' },
                        bgcolor: isCommunityAuthor(blogContent.authorType)
                          ? '#e2e8f0'
                          : '#1f2a44',
                        color: isCommunityAuthor(blogContent.authorType)
                          ? '#334155'
                          : '#ffffff',
                      }}
                    >
                      {getInitials(getAuthorDisplayName(blogContent))}
                    </Avatar>
                    <Typography variant='body2' sx={{ fontSize: 'inherit' }}>
                      {getAuthorDisplayName(blogContent)}
                    </Typography>
                  </Box>

                  {/* Published Date */}
                  {blogContent.publishedDate && (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <CalendarTodayIcon sx={{ fontSize: 16 }} />
                      <Typography variant='body2'>
                        {formatDate(blogContent.publishedDate)}
                      </Typography>
                    </Box>
                  )}

                  {/* Updated Date */}
                  {/* {blogContent.updatedDate &&
                    blogContent.updatedDate !== blogContent.publishedDate && (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <UpdateIcon sx={{ fontSize: 16 }} />
                        <Typography variant='body2'>
                          Updated {formatDate(blogContent.updatedDate)}
                        </Typography>
                      </Box>
                    )} */}

                  {/* Reading Time */}
                  {(blogContent.readingTime ||
                    calculateReadingTime(blogContent.content)) && (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <AccessTimeIcon sx={{ fontSize: 16 }} />
                      <Typography variant='body2'>
                        {blogContent.readingTime ||
                          calculateReadingTime(blogContent.content)}{' '}
                        min read
                      </Typography>
                    </Box>
                  )}

                  {/* Views */}
                  {/* {blogContent.views && (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <VisibilityIcon sx={{ fontSize: 16 }} />
                      <Typography variant='body2'>
                        {blogContent.views.toLocaleString()} views
                      </Typography>
                    </Box>
                  )} */}
                </Box>

                {/* Tags */}
                {blogContent.tags && blogContent.tags.length > 0 && (
                  <Box
                    sx={{
                      mt: { xs: 1.5, md: 2 },
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: { xs: 0.5, sm: 0.75, md: 1 },
                    }}
                  >
                    {blogContent.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size={isMobileScreen ? 'small' : 'medium'}
                        variant='outlined'
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                          height: { xs: 24, sm: 32 },
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* Blog Content */}
              <AnnotatedArticle
                documentId={blogContent?.slug || slug}
                documentType='blog'
                enableAnnotations={true}
                allowAnnotationEdits={!!user}
                readOnlyMessage='This document is in view-only mode. Log in to add or edit annotations.'
              >
                <Box
                  className='blog-content blog-content--detail'
                  sx={{
                    // Responsive typography for content
                    fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.0625rem' },
                    lineHeight: { xs: 1.6, md: 1.7 },
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                    },
                    '& pre': {
                      whiteSpace: 'pre-wrap',
                      overflowX: 'auto',
                      maxWidth: '100%',
                    },
                  }}
                >
                  {blogContent.content ? (
                    parseHtmlSanitizeAddTargetToLinks(
                      blogContent.content,
                      null, // searchQuery
                      `/blogs/${blogContent.slug}`,
                      {
                        fullDocumentContent: blogContent.content,
                        documentTitle:
                          stripHtml(blogContent.title) || 'Blog Post',
                        showSnackbar: showSnackbar,
                      },
                    )
                  ) : (
                    <Typography>No content available.</Typography>
                  )}
                </Box>
              </AnnotatedArticle>

              <Box sx={{ mt: { xs: 4, md: 5 } }}>
                <Typography
                  variant='h5'
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    fontSize: { xs: '1.2rem', sm: '1.35rem', md: '1.5rem' },
                  }}
                >
                  Comments ({comments.length})
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    mb: 2,
                    flexDirection: 'column',
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder='Share your thoughts on this blog...'
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    sx={themedTextFieldSx}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant='contained'
                      onClick={handleAddComment}
                      disabled={commentSubmitting || !commentText.trim()}
                      sx={{
                        backgroundColor: themeColors.primary,
                        color: 'white',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: themeColors.hover },
                      }}
                    >
                      {commentSubmitting ? 'Posting...' : 'Post Comment'}
                    </Button>
                  </Box>
                </Box>
                {commentsLoading ? (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', py: 2 }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : comments.length > 0 ? (
                  <Box>{renderCommentTree(comments)}</Box>
                ) : (
                  <Typography variant='body2' color='text.secondary'>
                    No comments yet. Be the first to comment.
                  </Typography>
                )}
              </Box>

              {/* Related Blogs */}
              {relatedBlogs.length > 0 && (
                <Box sx={{ mt: { xs: 4, md: 6 } }} className='related-articles'>
                  <Typography
                    variant='h5'
                    sx={{
                      mb: { xs: 2, md: 3 },
                      fontWeight: 600,
                      fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
                    }}
                  >
                    Related Articles
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: { xs: 1.5, md: 2 },
                    }}
                  >
                    {relatedBlogs.map((relatedBlog) => (
                      <Card
                        key={relatedBlog.slug}
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: { xs: 'none', sm: 'translateY(-2px)' },
                            boxShadow: { xs: 1, sm: 2 },
                          },
                          borderRadius: { xs: '8px', md: '12px' },
                        }}
                        onClick={() => {
                          if (!document.getElementById('main-popup-container')) {
                            navigate(`/blogs/${relatedBlog.slug}`);
                          }
                        }}
                      >
                        <CardContent
                          sx={{
                            display: 'flex',
                            gap: { xs: 1.5, sm: 2 },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            flexDirection: { xs: 'column', sm: 'row' },
                            p: { xs: 1.5, sm: 2, md: 2.5 },
                          }}
                        >
                          {relatedBlog.imageUrl && (
                            <Box
                              sx={{
                                width: { xs: '100%', sm: 100, md: 120 },
                                height: { xs: 180, sm: 70, md: 80 },
                                borderRadius: { xs: 1, md: 1.5 },
                                overflow: 'hidden',
                                flexShrink: 0,
                                backgroundColor: '#f3f4f6',
                              }}
                            >
                              <img
                                src={relatedBlog.imageUrl}
                                alt={stripHtml(relatedBlog.title)}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'fill',
                                }}
                              />
                            </Box>
                          )}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {/* Title with HTML rendering */}
                            <Typography
                              component='h3'
                              sx={{
                                fontWeight: 600,
                                mb: { xs: 0.5, md: 0.75 },
                                fontSize: {
                                  xs: '0.9375rem',
                                  sm: '1rem',
                                  md: '1.125rem',
                                },
                                lineHeight: { xs: 1.3, md: 1.4 },
                                display: '-webkit-box',
                                WebkitLineClamp: { xs: 2, md: 3 },
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                margin: 0,
                              }}
                            >
                              {stripHtml(relatedBlog.title) || 'Untitled'}
                            </Typography>
                            {relatedBlog.subtitle && (
                              <Typography
                                component='h4'
                                sx={{
                                  fontWeight: 400,
                                  mb: { xs: 0.75, md: 1 },
                                  fontSize: {
                                    xs: '0.8125rem',
                                    sm: '0.875rem',
                                    md: '0.9375rem',
                                  },
                                  lineHeight: { xs: 1.3, md: 1.4 },
                                  color: 'text.secondary',
                                  display: '-webkit-box',
                                  WebkitLineClamp: { xs: 1, md: 2 },
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  margin: 0,
                                }}
                              >
                                {stripHtml(relatedBlog.subtitle)}
                              </Typography>
                            )}

                            {/* Horizontal line separator */}
                            <Divider
                              sx={{
                                borderBottomWidth: '3px',
                                borderColor: '#00343d',
                                mb: { xs: 0.75, md: 1 },
                              }}
                            />
                            {/* Description with HTML rendering */}
                            <Box
                              component='div'
                              sx={{
                                color: 'text.secondary',
                                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                                lineHeight: { xs: 1.4, md: 1.5 },
                                display: '-webkit-box',
                                WebkitLineClamp: { xs: 2, sm: 3 },
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {parseHtmlSanitizeAddTargetToLinks(
                                relatedBlog.excerpt ||
                                  relatedBlog.description ||
                                  '',
                                null, // searchQuery
                                `/blogs/${relatedBlog.slug}`,
                                {
                                  fullDocumentContent:
                                    relatedBlog.excerpt ||
                                    relatedBlog.description ||
                                    '',
                                  documentTitle:
                                    stripHtml(relatedBlog.title) ||
                                    'Related Article',
                                  showSnackbar: showSnackbar,
                                },
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Back to Top FAB */}
          <Box
            sx={{
              position: 'fixed',
              bottom: { xs: 16, md: 20 },
              right: { xs: 16, md: 20 },
              zIndex: 1000,
            }}
          >
            <Zoom in={showBackToTop}>
              {isMediumScreen ? (
                <Fab
                  size={isMobileScreen ? 'small' : 'medium'}
                  aria-label='Scroll back to top'
                  onClick={scrollToTop}
                  sx={{
                    backgroundColor: themeColors.primary,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: themeColors.hover,
                    },
                    width: { xs: 48, sm: 56 },
                    height: { xs: 48, sm: 56 },
                  }}
                >
                  <KeyboardArrowUpIcon
                    fontSize={isMobileScreen ? 'small' : 'medium'}
                  />
                </Fab>
              ) : (
                <Fab
                  variant='extended'
                  size='medium'
                  aria-label='Scroll back to top'
                  onClick={scrollToTop}
                  sx={{
                    backgroundColor: themeColors.primary,
                    color: 'white',
                    '&:hover': { backgroundColor: themeColors.hover },
                    paddingLeft: { xs: 1.5, md: 2 },
                    paddingRight: { xs: 1.5, md: 2 },
                    borderRadius: buttonBorderRadius,
                    fontSize: { xs: '0.8125rem', md: '0.875rem' },
                  }}
                >
                  <KeyboardArrowUpIcon sx={{ mr: 1 }} />
                  <Typography
                    variant='button'
                    sx={{
                      fontWeight: 'bold',
                      fontSize: 'inherit',
                    }}
                  >
                    Back to Top
                  </Typography>
                </Fab>
              )}
            </Zoom>
          </Box>
        </Paper>
      </Container>

      <Dialog
        open={Boolean(deleteDialogCommentId)}
        onClose={closeDeleteCommentDialog}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#111827' }}>
          Delete Comment
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#475569' }}>
            Delete this comment and all of its replies? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeDeleteCommentDialog}
            disabled={Boolean(commentDeleteSubmittingFor)}
            sx={{ textTransform: 'none', color: '#64748b' }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={confirmDeleteComment}
            disabled={Boolean(commentDeleteSubmittingFor)}
            sx={{
              textTransform: 'none',
              backgroundColor: '#b42318',
              '&:hover': { backgroundColor: '#991b1b' },
            }}
          >
            {commentDeleteSubmittingFor ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={1500}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: { xs: 16, md: 24 },
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{
            mt: { xs: 4, md: 5.5 },
            width: { xs: '90vw', sm: 'auto' },
            maxWidth: { xs: 400, sm: 600 },
            fontSize: { xs: '0.875rem', md: '1rem' },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default BlogDetail
